import type { RouteGroup } from '@usealloy/daffy-to-frontend-dashboard-rollout';

/** Copy for one banner state. `body` only exists in the off state, which is a two-clause sentence. */
interface BannerStateCopy {
  /** Trailing clause, rendered between `lead` and the learn-more link (if any). */
  body?: string;
  /** The bolded opening clause. */
  lead: string;
}

interface BannerCopy {
  /**
   * Learn-more link. Omitted while an area has no help article yet — that area's off-state
   * copy must then read as a complete sentence on its own, not trail into a link.
   */
  learnMore?: {
    /** Per state: the off-state label closes a sentence; the on-state label stands alone. */
    labels: { off: string; on: string };
    url: string;
  };
  /** Shown in FE2.0, where the agent is already in the beta. */
  on: BannerStateCopy;
  /** Shown in legacy Daffy, where the agent has not opted in yet. */
  off: BannerStateCopy;
}

/**
 * Per-area banner copy, keyed by the matched route group's `featureFlag`. Total, not partial: a
 * newly-registered route group without copy fails to typecheck rather than silently rendering
 * nothing.
 *
 * Deliberately duplicated from frontend-dashboard's copy of the same map rather than shared:
 * each app only ever renders one of the two states, and the two BetaPreferencesProviders are
 * already duplicated for the same reason. See FRONTEND-723's plan, Constraint 1.
 */
export const BANNER_COPY: Record<RouteGroup['featureFlag'], BannerCopy> = {
  'beta-routes-enabled': {
    learnMore: {
      labels: { off: 'learn more first', on: 'Learn more' },
      url: 'https://help.alloy.com/hc/en-us/sections/54549209384219-Application-2-0-Beta',
    },
    off: {
      lead: 'New Application Review Experience is here',
      body: 'Open Beta is now available — try it out, or',
    },
    on: {
      lead: "You're using our Applications Beta.",
    },
  },
  // TODO(FRONTEND-789): placeholder copy — no Figma strings exist for Investigations yet.
  // Swap for real copy (and add `learnMore` once a help article exists) when design delivers it.
  'beta-investigations': {
    off: {
      lead: 'New Investigations Review Experience is here',
      body: 'Open Beta is now available — try it out.',
    },
    on: {
      lead: "You're using our Investigations Beta.",
    },
  },
};

/** Label for the Pendo-anchored feedback affordance. */
export const FEEDBACK_LABEL = 'Have Feedback?';

/**
 * Per-area Pendo anchors for the feedback affordance, keyed by the matched route group's
 * `featureFlag`. Pendo configuration is owned outside this repo — the banner only exposes
 * the anchor.
 */
export const FEEDBACK_PENDO_ANCHOR: Record<RouteGroup['featureFlag'], string> = {
  'beta-routes-enabled': 'beta-banner-feedback-applications',
  'beta-investigations': 'beta-banner-feedback-investigations',
};

/** Height of the bar, from the Figma design. */
export const BANNER_HEIGHT = '48px';
